import {DropdownMenu, Icon, type IconName} from '@wandb/components';
import {useRef, useState} from 'react';

import {type Account, AccountType} from '../account/accountModel';
import css from './CLILogin.module.css';

/** `memberCount` is negative when unknown. */
export type OrganizationOption = Pick<
  Account,
  'name' | 'accountType' | 'memberCount'
>;

const ACCOUNT_ICONS: Record<AccountType, IconName> = {
  [AccountType.Personal]: 'user-profile-personal',
  [AccountType.Corporate]: 'organization-corporate',
  [AccountType.Academic]: 'education-academic',
};

const accountDescription = (option: OrganizationOption): string => {
  if (option.accountType === AccountType.Personal) {
    return 'Free';
  }
  if (option.memberCount < 0) {
    return option.accountType;
  }
  const users = option.memberCount === 1 ? 'user' : 'users';
  return `${option.accountType} · ${option.memberCount} ${users}`;
};

const AccountLabel = ({
  option,
  isSelected,
  maxWidth,
}: {
  option: OrganizationOption;
  isSelected: boolean;
  maxWidth: string;
}) => (
  <div className={css.accountButton} data-test={`account-${option.name}`}>
    <div
      className={`${css.accountIcon} ${
        isSelected ? css.accountIconSelected : css.accountIconOther
      }`}>
      <Icon icon={ACCOUNT_ICONS[option.accountType]} color="currentcolor" />
    </div>
    <div className={css.accountText}>
      <span className={css.accountName} style={{maxWidth}} title={option.name}>
        {option.name}
      </span>
      <span className={css.accountInfo}>{accountDescription(option)}</span>
    </div>
  </div>
);

export const OrganizationField = ({
  options,
  value,
  onChange,
}: {
  options: OrganizationOption[];
  value: string;
  onChange: (organization: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = options.find(option => option.name === value) ?? {
    name: value,
    accountType: AccountType.Corporate,
    memberCount: -1,
  };
  const others = options.filter(option => option.name !== value);
  const canSwitch = others.length > 0;

  const selectedCard = (
    <>
      <AccountLabel
        option={selected}
        isSelected
        maxWidth={canSwitch ? '150px' : '180px'}
      />
      {canSwitch && (
        <Icon
          icon={isOpen ? 'chevron-up' : 'chevron-down'}
          color="moon-500"
          size="sm"
        />
      )}
    </>
  );

  if (!canSwitch) {
    return <div className={css.organization}>{selectedCard}</div>;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Choose organization. Current organization: ${value}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
        className={`${css.organization} ${css.organizationTrigger}`}>
        {selectedCard}
      </button>
      <DropdownMenu.Content
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onClose={() => setIsOpen(false)}
        triggerRef={triggerRef}
        placement="bottom end"
        aria-label="Other organizations">
        <DropdownMenu.Header>Other organizations</DropdownMenu.Header>
        {others.map(option => (
          <DropdownMenu.Item
            key={option.name}
            textValue={option.name}
            onAction={() => {
              onChange(option.name);
              setIsOpen(false);
            }}>
            <div className={css.menuItem}>
              <AccountLabel
                option={option}
                isSelected={false}
                maxWidth="210px"
              />
              <span className={css.retry} aria-hidden="true">
                <Icon icon="retry-swap" color="currentcolor" />
              </span>
            </div>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </>
  );
};
